import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { Subject, combineLatest } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ProyectosService } from "src/app/proyectos/proyectos.service";
import ApexCharts, { type ApexOptions } from "apexcharts";

interface ResourceEffort {
  name: string;
  used: number;
  total: number;
  tone: "primary";
}

interface SubFaseItem {
  nombre: string;
  completado: boolean;
  opcional?: boolean;
}

interface HistoryItem {
  date: string;
  progress: number;
  owner: string;
  detail: string;
  avatar: string;
  fase: string;
  avanceDesarrollo: number | null;
  subfases: SubFaseItem[];
}

interface ProgressBarItem {
  label: string;
  progress: number;
  isCurrent: boolean;
}

interface ComentarioAvance {
  id: number;
  descripcion: string;
  fecha: string;
  nombreUsuario: string;
  porcentajeRegistrado: number;
  fase?: string;
  avanceDesarrollo?: number;
  qaSubFases?: any[];
  prodSubFases?: any[];
}

interface DetalleSeguimientoResponse {
  id: string;
  idProyecto: string;
  title: string;
  lead: string;
  client: string;
  fechaInicio: string | null;
  fechaFin: string | null;
  avancePlaneado: number;
  avanceReal: number;
  horasTotales: number;
  horasReales: number;
  comentariosAvances: ComentarioAvance[];
}

interface DetailProject {
  idProyecto: string;
  title: string;
  lead: string;
  leadAvatar: string;
  duration: string;
  realProgress: number;
  scheduleNote: string;
  plannedLabel: string;
  horasReales: number;
  horasTotales: number;
  currentFase: string;
  resourceEffort: ResourceEffort[];
  history: HistoryItem[];
  progressBars: ProgressBarItem[];
}

@Component({
  selector: "app-detalle-proyecto",
  standalone: true,
  templateUrl: "./detalle-proyecto.component.html",
  styleUrls: ["./detalle-proyecto.component.css"],
  imports: [CommonModule, RouterModule, MatButtonModule, MatIconModule],
})
export class DetalleProyectoComponent implements OnInit, OnDestroy {
  readonly FASE_COLORS: Record<string, { main: string; light: string }> = {
    DESARROLLO: { main: '#083e91', light: '#e3eeff' },
    QA:         { main: '#6c58a7', light: '#ede8fa' },
    PRODUCCION: { main: '#212121', light: '#eeeeee' },
  };

  readonly FASE_LABELS: Record<string, string> = {
    DESARROLLO: 'Desarrollo',
    QA: 'QA',
    PRODUCCION: 'Producción',
  };

  readonly FASES = [
    { value: 'DESARROLLO', label: 'Desarrollo', icon: 'code',          peso: 85 },
    { value: 'QA',         label: 'QA',         icon: 'science',       peso: 10 },
    { value: 'PRODUCCION', label: 'Producción',  icon: 'rocket_launch', peso: 5  },
  ];

  readonly FASE_ICONS: Record<string, string> = {
    DESARROLLO: 'code',
    QA: 'science',
    PRODUCCION: 'rocket_launch',
  };

  projectId = "";
  readonly defaultAvatar = "assets/img/faces/profile.png";
  returnQueryParams: Record<string, string | number | null> = {};
  private readonly destroy$ = new Subject<void>();

  isLoading = false;
  private progressChart: ApexCharts | null = null;

  detailProject: DetailProject = {
    idProyecto: "",
    title: "",
    lead: "",
    leadAvatar: this.defaultAvatar,
    duration: "Sin fechas registradas",
    realProgress: 0,
    scheduleNote: "Sin información de avance disponible",
    plannedLabel: "Avance planeado vs avance real del proyecto",
    horasReales: 0,
    horasTotales: 0,
    currentFase: "DESARROLLO",
    resourceEffort: [
      { name: "Total", used: 0, total: 0, tone: "primary" },
    ],
    history: [],
    progressBars: [],
  };

  constructor(
    private route: ActivatedRoute,
    private proyectosService: ProyectosService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.renderizarGraficaProgreso();
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, queryParams]) => {
        this.projectId = params.get("projectId") || "";
        this.returnQueryParams = {
          estatus: queryParams.get("estatus"),
          idCliente: this.parseNullableNumber(queryParams.get("idCliente")),
          idLider: this.parseNullableNumber(queryParams.get("idLider")),
        };
        this.consultarDetalle();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.progressChart?.destroy();
  }

  private renderizarGraficaProgreso(): void {
    const bars = this.detailProject.progressBars;
    const options: ApexOptions = {
      series: [{ name: "Avance", data: bars.map((b) => b.progress) }],
      chart: { type: "bar", height: 220, toolbar: { show: false } },
      plotOptions: {
        bar: { distributed: true, borderRadius: 6, columnWidth: "55%" },
      },
      colors: bars.map((b) => (b.isCurrent ? "#0f4e8a" : "#9bb9d8")),
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val}%`,
        style: { fontSize: "10px" },
      },
      xaxis: {
        categories: bars.map((b) => b.label),
        labels: { style: { fontSize: "9px" } },
      },
      yaxis: {
        max: 100,
        labels: { formatter: (val: number) => `${val}%` },
      },
      legend: { show: false },
      tooltip: {
        y: { formatter: (val: number) => `${val}% de avance` },
      },
      noData: {
        text: "Sin historial de avances registrado.",
        style: { color: "#97a3b8", fontSize: "12px" },
      },
    };

    const chartElement = document.querySelector<HTMLElement>("#progress-chart");
    if (this.progressChart) {
      this.progressChart.updateOptions(options);
    } else if (chartElement) {
      this.progressChart = new ApexCharts(chartElement, options);
      this.progressChart.render();
    }
  }

  getFaseColor(fase: string): string {
    return this.FASE_COLORS[fase]?.main ?? this.FASE_COLORS['DESARROLLO'].main;
  }

  getFaseLight(fase: string): string {
    return this.FASE_COLORS[fase]?.light ?? this.FASE_COLORS['DESARROLLO'].light;
  }

  getFaseLabel(fase: string): string {
    return this.FASE_LABELS[fase] ?? fase;
  }

  getFaseIcon(fase: string): string {
    return this.FASE_ICONS[fase] ?? 'code';
  }

  isFasePasada(faseValue: string): boolean {
    const order = ['DESARROLLO', 'QA', 'PRODUCCION'];
    return order.indexOf(this.detailProject.currentFase) > order.indexOf(faseValue);
  }

  stepStyle(faseValue: string): Record<string, string> {
    const c = this.FASE_COLORS[faseValue] ?? this.FASE_COLORS['DESARROLLO'];
    return { '--step-color': c.main, '--step-light': c.light };
  }

  getResourceProgress(item: ResourceEffort): number {
    if (!item.total) {
      return 0;
    }
    return Math.min(Math.round((item.used / item.total) * 100), 100);
  }

  private consultarDetalle(): void {
    if (!this.projectId) {
      return;
    }

    this.isLoading = true;
    this.proyectosService
      .consultarDetalleSeguimientoProyecto(this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: DetalleSeguimientoResponse) => {
          this.detailProject = this.mapDetailProject(response);
          this.isLoading = false;
          this.renderizarGraficaProgreso();
          this.cdr.detectChanges();
        },
        (error) => {
          console.error(
            "Error al consultar el detalle de seguimiento del proyecto:",
            error
          );
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      );
  }

  private mapDetailProject(response: DetalleSeguimientoResponse): DetailProject {
    const horasTotales = Number(response?.horasTotales ?? 0);
    const horasReales = Number(response?.horasReales ?? 0);
    const avancePlaneado = Number(response?.avancePlaneado ?? 0);
    const avanceReal = Number(response?.avanceReal ?? 0);
    const scheduleDelta = avanceReal - avancePlaneado;
    // backend sorts desc; first entry with fase = most recent phase
    const conFase = (response?.comentariosAvances ?? []).filter(c => c.fase);
    const currentFase = conFase.length > 0 ? (conFase[0].fase ?? 'DESARROLLO') : 'DESARROLLO';

    const history = (response?.comentariosAvances ?? []).map((comentario) => {
      const rawSubs: any[] | undefined =
        comentario.fase === 'QA'         ? comentario.qaSubFases :
        comentario.fase === 'PRODUCCION' ? comentario.prodSubFases : undefined;
      const subfases: SubFaseItem[] = (rawSubs ?? []).map((sf: any) => ({
        nombre:    sf.nombre   ?? '',
        completado: Boolean(sf.completado),
        opcional:  Boolean(sf.opcional),
      }));
      return {
        date:     comentario.fecha           || 'Sin fecha',
        progress: Number(comentario.porcentajeRegistrado ?? 0),
        owner:    comentario.nombreUsuario   || 'Sin responsable',
        detail:   comentario.descripcion     || 'Sin detalle',
        avatar:   this.defaultAvatar,
        fase:             comentario.fase ?? 'DESARROLLO',
        avanceDesarrollo: comentario.fase === 'DESARROLLO' || !comentario.fase
          ? (comentario.avanceDesarrollo ?? null)
          : null,
        subfases,
      };
    });
    const progressBars = [...history]
      .reverse()
      .map((item, index, array) => ({
        label: item.date,
        progress: item.progress,
        isCurrent: index === array.length - 1,
      }));

    return {
      idProyecto: response?.idProyecto ?? "Sin ID de proyecto",
      title: response?.title ?? "Proyecto sin nombre",
      lead: response?.lead ?? "Sin líder asignado",
      leadAvatar: this.defaultAvatar,
      duration: this.formatDuration(response?.fechaInicio, response?.fechaFin),
      realProgress: avanceReal,
      scheduleNote:
        scheduleDelta >= 0
          ? `${scheduleDelta}% sobre lo planeado`
          : `${Math.abs(scheduleDelta)}% debajo de lo planeado`,
      plannedLabel: `Cliente: ${response?.client ?? "Sin cliente"}`,
      horasReales,
      horasTotales,
      currentFase,
      resourceEffort: [
        {
          name: "Total",
          used: horasReales,
          total: horasTotales,
          tone: "primary",
        },
      ],
      history,
      progressBars,
    };
  }

  private formatDuration(
    fechaInicio?: string | null,
    fechaFin?: string | null
  ): string {
    if (!fechaInicio && !fechaFin) {
      return "Sin fechas registradas";
    }

    const inicio = fechaInicio
      ? new Date(fechaInicio).toLocaleDateString("es-MX")
      : "Sin inicio";
    const fin = fechaFin
      ? new Date(fechaFin).toLocaleDateString("es-MX")
      : "Sin fin";

    return `${inicio} - ${fin}`;
  }

  private parseNullableNumber(value: string | null): number | null {
    if (value == null || value === "") {
      return null;
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }
}
